import { Chart, TooltipModel } from "chart.js";
import { createRoot, Root } from "react-dom/client";
import { ChartSeries, CustomTooltipComponent } from "./types";

interface TooltipElement extends HTMLDivElement {
  _reactRoot?: Root;
}

const TOOLTIP_CONSTANTS = {
  OFFSET_X: 10,
  LEFT_PADDING: 40,
  RIGHT_PADDING: 20,
  TOP_PADDING: 20,
  BOTTOM_PADDING: 50,
  TRANSITION:
    "left 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), top 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
};

function getTooltipElement(): TooltipElement {
  let tooltipEl = document.getElementById("chartjs-tooltip") as TooltipElement;
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.zIndex = "1000";
    document.body.append(tooltipEl);
  }
  return tooltipEl;
}

function renderTooltip(
  tooltipEl: TooltipElement,
  customTooltip: CustomTooltipComponent,
  tooltip: TooltipModel<"line" | "bar">,
  valueTooltipFormatter: (value: number) => string,
  showSeriesNames: boolean,
  series: ChartSeries[],
  customTooltipTitles?: string[],
): void {
  const customTooltipElement = customTooltip({
    tooltipItems: tooltip.dataPoints,
    valueTooltipFormatter,
    showSeriesNames,
    series,
    customTooltipTitles,
  });

  const existingRoot = tooltipEl._reactRoot;
  if (existingRoot) {
    existingRoot.render(customTooltipElement);
  } else {
    const root = createRoot(tooltipEl);
    tooltipEl._reactRoot = root;
    root.render(customTooltipElement);
  }
}

function positionTooltip(
  tooltipEl: HTMLDivElement,
  context: { chart: Chart; tooltip: TooltipModel<"line" | "bar"> },
  tooltip: TooltipModel<"line" | "bar">,
): void {
  const position = context.chart.canvas.getBoundingClientRect();

  // Make tooltip visible temporarily to get dimensions
  tooltipEl.style.opacity = "1";
  tooltipEl.style.visibility = "hidden";

  const tooltipWidth = tooltipEl.offsetWidth;
  const tooltipHeight = tooltipEl.offsetHeight;

  const chartLeft = position.left + window.pageXOffset;
  const chartRight = chartLeft + position.width;
  const chartTop = position.top + window.pageYOffset;
  const chartBottom = chartTop + position.height;

  const cursorX = chartLeft + tooltip.caretX;
  const cursorY = chartTop + tooltip.caretY;

  let left = cursorX + TOOLTIP_CONSTANTS.OFFSET_X;
  let top = cursorY - tooltipHeight / 2;

  if (left + tooltipWidth > chartRight - TOOLTIP_CONSTANTS.RIGHT_PADDING) {
    left = cursorX - tooltipWidth - TOOLTIP_CONSTANTS.OFFSET_X;
  }

  left = Math.max(
    chartLeft + TOOLTIP_CONSTANTS.LEFT_PADDING,
    Math.min(left, chartRight - tooltipWidth - TOOLTIP_CONSTANTS.RIGHT_PADDING),
  );

  top = Math.max(
    chartTop + TOOLTIP_CONSTANTS.TOP_PADDING,
    Math.min(
      top,
      chartBottom - tooltipHeight - TOOLTIP_CONSTANTS.BOTTOM_PADDING,
    ),
  );

  tooltipEl.style.visibility = "visible";
  tooltipEl.style.left = left + "px";
  tooltipEl.style.top = top + "px";
  tooltipEl.style.transition = TOOLTIP_CONSTANTS.TRANSITION;
}

export function createCustomTooltipHandler(
  customTooltip: CustomTooltipComponent,
  valueTooltipFormatter: (value: number) => string,
  showSeriesNames: boolean,
  series: ChartSeries[],
  customTooltipTitles?: string[],
) {
  return (context: { chart: Chart; tooltip: TooltipModel<"line" | "bar"> }) => {
    const { tooltip } = context;
    const tooltipEl = getTooltipElement();

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      tooltipEl.style.transition = "opacity 0.1s ease-out";
      return;
    }

    if (tooltip.body && series) {
      renderTooltip(
        tooltipEl,
        customTooltip,
        tooltip,
        valueTooltipFormatter,
        showSeriesNames,
        series,
        customTooltipTitles,
      );
    }

    positionTooltip(tooltipEl, context, tooltip);
  };
}
